import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useReportGenerator, { isReport, ReportType } from 'src/app/hooks/report-generator/report-generator-hook'
import useAccountBalanceReportCrud from './account-balance-report-crud'
import ReportViewer from 'src/app/components/report-viewer/report-viewer'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

type AccountBalanceBill = { billId: string; productionAssignedIds?: string[] }

type AccountBalanceReportViewerProps = {
  bills?: (AccountBalanceBill | undefined)[]
  selectedDepositAssignedIds?: string[]
  propertyId: string
  open: boolean
  setClose: () => void
}

export default function AccountBalanceReportViewer(props: AccountBalanceReportViewerProps) {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const { bills, propertyId, open, setClose, selectedDepositAssignedIds } = props
  const generateReport = useReportGenerator()
  const showError = useErrorSnackBar()

  const filteredBills = (bills?.filter((bill) => bill !== undefined) ?? []) as AccountBalanceBill[]
  const [loading, setLoading] = useState(false)

  const { loading: loadingCrud, depositAssignedIds } = useAccountBalanceReportCrud(filteredBills?.map((bill) => bill?.billId) ?? [])
  const [report, setReport] = useState<string | undefined>()

  useEffect(() => {
    const doReport = async () => {
      const depositAssignedToPrint = selectedDepositAssignedIds ? selectedDepositAssignedIds : depositAssignedIds
      if (bills || depositAssignedToPrint !== null) {
        setLoading(true)
        const reportBlob = await generateReport({
          body: {
            tenantId: tenantId,
            propertyId: propertyId,
            bills: filteredBills,
            depositIds: depositAssignedToPrint ?? undefined, // the variables is called depositIds but it requires the depositAssignedIds
          },
          type: ReportType.ACCOUNT_BALANCE,
        })
        setLoading(false)
        if (isReport(reportBlob)) {
          setReport(reportBlob.file)
        } else {
          showError(reportBlob.message)
          setClose()
        }
      }
    }
    doReport()
  }, [JSON.stringify(bills), JSON.stringify(depositAssignedIds), JSON.stringify(selectedDepositAssignedIds), tenantId, propertyId])

  return (
    <>
      {bills !== undefined && (
        <ReportViewer isLoading={loading || loadingCrud} title={t('pms.components.report_viewer.account_balance')} open={open} setClose={setClose} reportFile={report} size="sm" />
      )}
    </>
  )
}
